import React, { useState, useCallback, useEffect } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import usersAPI from '../../api/users';

const CACHE = {};

const UsersSelect = ({
  onChange = () => {},
  defaultValue,
  multiple,
  ...otherProps
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(defaultValue || []);

  const limit = 50;

  useEffect(() => {
    if (!query.trim().length) {
      handleSearch(query);
    }
  }, [query]);

  const handleInputChange = (query) => setQuery(query);

  const handleSearch = useCallback((query) => {
    try {
      const additionalParams = {};

      if (query.trim().length > 0) {
        if (CACHE[query]) {
          setOptions(CACHE[query].options);
          return;
        }

        additionalParams.search = query.trim();
      }

      setIsLoading(true);

      usersAPI
        .getUsers({
          ...additionalParams,
          page: 1,
          limit,
        })
        .then((response) => {
          const {
            data: { users, totalDocs },
          } = response;

          CACHE[query] = {
            page: 1,
            options: users.map(({ _id, email }) => ({ _id, email })),
            totalDocs,
          };

          setOptions(users);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  }, []);

  const handlePagination = (e, showResults) => {
    try {
      const cachedQuery = CACHE[query];

      if (
        cachedQuery.options.length > showResults ||
        cachedQuery.options.length === cachedQuery.totalDocs
      ) {
        return;
      }

      setIsLoading(true);
      const nextPage = cachedQuery.page + 1;

      usersAPI
        .getUsers({
          search: query.trim(),
          page: nextPage,
          limit,
        })
        .then((response) => {
          const {
            data: { users, totalDocs },
          } = response;

          CACHE[query] = {
            page: nextPage,
            options: [
              ...cachedQuery.options,
              ...users.map(({ _id, email }) => ({ _id, email })),
            ],
            totalDocs,
          };

          setOptions(users);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  if (otherProps.readOnly) {
    otherProps.inputProps = {
      readOnly: true,
    };
    delete otherProps.readOnly;
  }

  return (
    <AsyncTypeahead
      id='users-select'
      placeholder='Search for users...'
      labelKey='email'
      multiple={multiple === false ? false : true}
      options={options}
      paginate
      isLoading={isLoading}
      maxResults={limit - 1}
      minLength={0}
      onInputChange={handleInputChange}
      onSearch={handleSearch}
      onPaginate={handlePagination}
      onChange={(e) => {
        setSelected(e);
        onChange(e);
      }}
      {...otherProps}
      selected={selected}
    />
  );
};

export default UsersSelect;
